import React from 'react';
import FeatherIcon from 'feather-icons-react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Space } from 'antd';
import { ButtonConfirm } from './styled';
import { profileTypes } from '../../../config/constants/userConstants';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';
import { Button } from '../../buttons/buttons';
import { useNotifications } from '../../../hooks/useNotifications';
import { solutionConstants } from '../../../config/constants/solutionsConstants';
import { incidentFinalStates, incidentSupportStates } from '../../../config/constants/incidentsStatus';

const profilePermissions = {
  [profileTypes.TECHNICIAN.id]: {
    enableVideoConsultation: true,
  },
  [profileTypes.INTERESTED.id]: {
    canReschedule: false,
    canConfirmServices: false,
  },
  DEFAULT: {
    canEditServices: true,
    canCancelServices: true,
  },
};

export function EventActions({ event, editEvent, onCancelEvent, confirmService, onFinish }) {
  const { showConfirmNotification } = useNotifications();
  const { translate: t } = useDictionaryTranslation();
  const { accountModules, currentProfile, accountData, canCancelServicesPermission } = useSelector((state) => ({
    currentProfile: state.auth?.currentUser?.profile?.idProfile,
    accountData: state.auth.accountData,
    accountModules: state.auth?.accountModules,
    canCancelServicesPermission: state.auth?.permissions?.canCancelServices,
  }));
  const accountsCanEdit =
    accountData?.solutionId === solutionConstants.TAPPTUS.id ||
    accountData?.solutionId === solutionConstants.MIEMERGENCIA.id;
  const enabledConfirm = accountData?.solutionId === solutionConstants.MICITA.id;
  const currentProfilePermissions = profilePermissions[currentProfile] ?? profilePermissions.DEFAULT;
  const isFinalState = incidentFinalStates.includes(event.data?.status);
  const isSupportStates = accountsCanEdit && incidentSupportStates.includes(event.data?.status);
  const showActions = !isFinalState || isSupportStates;
  const settingActions = {
    showVideoConsultation:
      accountData?.isAvailable &&
      accountModules.videoConsultationRoom &&
      event.data?.infoServiceType?.isVideoCallEnabled,
    showConfirm: enabledConfirm && accountData?.isAvailable && currentProfilePermissions.canConfirmServices,
    showEdit: accountData?.isAvailable && currentProfilePermissions.canEditServices && showActions,
    showCancel:
      accountData?.isAvailable && currentProfilePermissions.canCancelServices && canCancelServicesPermission,
  };

  const reschedule = (item) => () => {
    editEvent(item);
    onFinish();
  };

  const cancelEvent = (item) => () => {
    onCancelEvent(item);
    onFinish();
  };

  const confirmServiceHandler = (serviceId) => {
    return () => {
      showConfirmNotification({
        title: t('areYouSure'),
        content: t('confirmedServiceDescription'),
        onOk: async () => {
          try {
            await confirmService(serviceId);
            onFinish();
          } catch (error) {
            console.error(error);
          }
        },
      });
    };
  };

  if (!showActions) {
    return null;
  }

  return (
    <div style={{ textAlign: 'right', marginTop: '20px' }}>
      {!isSupportStates ? (
        <Space size="small">
          {settingActions.showVideoConsultation ? (
            <Link to="/video-consultation">
              <Button form="consultation" key="submit" htmlType="submit" className="video">
                <FeatherIcon icon="video" size={40} className="icon" />
                {t('videoconsultation')}
              </Button>
            </Link>
          ) : null}
          {settingActions.showConfirm ? (
            <ButtonConfirm
              confirm={event.confirmedByUser}
              disabled={event.confirmedByUser}
              type="secondary"
              onClick={event.confirmedByUser ? null : confirmServiceHandler(event.id)}
            >
              {t('confirm')}
            </ButtonConfirm>
          ) : null}
          {settingActions.showEdit ? (
            <Button className="btnEdit" htmlType="submit" type="secondary" onClick={reschedule(event)}>
              {t('edit')}
            </Button>
          ) : null}
          {settingActions.showCancel ? (
            <Button htmlType="submit" type="danger" onClick={cancelEvent(event)}>
              {t('cancel')}
            </Button>
          ) : null}
        </Space>
      ) : (
        <Space size="small">
          {settingActions.showEdit ? (
            <Button className="btnEdit" htmlType="submit" type="secondary" onClick={reschedule(event)}>
              {t('edit')}
            </Button>
          ) : null}
        </Space>
      )}
    </div>
  );
}

EventActions.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    privateId: PropTypes.string,
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    assignedUser: PropTypes.string,
    assignedServiceType: PropTypes.string,
    assignedFinalCustomer: PropTypes.object,
    confirmedByUser: PropTypes.bool,
    isGroup: PropTypes.bool,
    events: PropTypes.arrayOf(PropTypes.object),
    data: PropTypes.object,
  }),
  onFinish: PropTypes.func,
  editEvent: PropTypes.func,
  onCancelEvent: PropTypes.func,
  confirmService: PropTypes.func,
};
